const SEEN_KEYS_STORAGE_KEY = '__sentry_replay_seen_keys';

let inMemorySeenKeys = new Set();

const getSessionStorage = () => {
    try {
        return globalThis.sessionStorage;
    } catch {
        return;
    }
};

export const loadSeenKeys = () => {
    const storage = getSessionStorage();
    if (!storage) {
        return inMemorySeenKeys;
    }
    try {
        const raw = storage.getItem(SEEN_KEYS_STORAGE_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch {
        return inMemorySeenKeys;
    }
};

export const persistSeenKeys = (keys) => {
    const storage = getSessionStorage();
    if (!storage) {
        inMemorySeenKeys = keys;
        return;
    }
    try {
        storage.setItem(SEEN_KEYS_STORAGE_KEY, JSON.stringify([...keys]));
    } catch {
        inMemorySeenKeys = keys;
    }
};

export const replayErrorSignature = (event) => {
    const exceptionValue = event.exception?.values?.[0];
    const type = exceptionValue?.type || event.message || 'unknown';
    const topFrame = exceptionValue?.stacktrace?.frames?.at?.(-1);
    return `${type}|${topFrame?.function || ''}|${topFrame?.filename || ''}`;
};

const NON_ACTIONABLE_REPLAY_ERROR_PATTERNS = [
    /failed to fetch/i,
    /networkerror/i,
    /network request failed/i,
    /load failed/i,
    /content security policy/i,
    /refused to connect/i,
    /aborterror/i,
];

export const isNonActionableReplayError = (event) => {
    const exception = event?.exception?.values?.[0];
    const haystack = [
        exception?.type,
        exception?.value,
        event?.message,
    ].filter(Boolean).join(' ');

    return NON_ACTIONABLE_REPLAY_ERROR_PATTERNS.some((pattern) => pattern.test(haystack));
};

export const beforeErrorSampling = (event) => {
    const level = event.level || 'error';
    if (!['fatal', 'error'].includes(level)) {
        return false;
    }
    if (isNonActionableReplayError(event)) {
        return false;
    }

    const path = globalThis.location?.pathname || 'unknown';
    const key = `${path}::${replayErrorSignature(event)}`;

    const seenKeys = loadSeenKeys();
    if (seenKeys.has(key)) {
        return false;
    }

    seenKeys.add(key);
    persistSeenKeys(seenKeys);
    return true;
};

export const buildSentryOptions = (environment, sentry) => {
    const replayOptions = {
        beforeErrorSampling,
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false,
    };

    // TODO(sentry-replay-rollout): re-enable prod-only gate before merge to prod
    // if (!environment.isProd) return null  // or skip replay integration
    const browserTracing = sentry.browserTracingIntegration();
    const replay = sentry.replayIntegration(replayOptions);

    return {
        dsn: environment.dsn,
        environment: environment.environment,
        integrations: [browserTracing, replay],
        // TODO(sentry-replay-rollout): keep replaysSessionSampleRate at 0 for cost; set 1.0 only for "see any replay" smoke tests
        replaysOnErrorSampleRate: 1,
        replaysSessionSampleRate: 0,
    };
};
